import type { ApolloError } from "@apollo/client";
import { useLazyQuery } from "@apollo/client";
import type { PureAbility } from "@casl/ability";
import { useCallback, useEffect } from "react";

import { organizationLevelPermissions } from "../config";
import { GET_ORGANIZATION_DATA } from "pages/organization/queries";
import { Logger } from "utils/logger";

interface IOrgPermissionsResponse {
  error: ApolloError | undefined;
  loading: boolean;
  permissions: PureAbility<string>;
  permissionMethods: {
    can: (action: string) => boolean;
    canMutate: (action: string) => boolean;
    canResolve: (action: string) => boolean;
    refresh: (orgName: string) => Promise<void>;
  };
}

const useOrgPermissions = (): IOrgPermissionsResponse => {
  const [getOrgPermissions, { data: orgData, loading, error }] = useLazyQuery(
    GET_ORGANIZATION_DATA,
    {
      fetchPolicy: "cache-first",
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((err): void => {
          Logger.error("Couldn't load organization data", err);
        });
      },
    },
  );

  useEffect((): void => {
    if (orgData?.organizationId.permissions) {
      organizationLevelPermissions.update(
        orgData.organizationId.permissions.map(
          (permission): { action: string } => ({
            action: permission ?? "",
          }),
        ),
      );
    }
  }, [orgData]);

  const refresh = useCallback(
    async (organizationName: string): Promise<void> => {
      await getOrgPermissions({
        variables: {
          canGetGroups: true,
          organizationName: organizationName.toLowerCase(),
        },
      });
    },
    [getOrgPermissions],
  );

  const can = useCallback(
    (permission: string): boolean =>
      organizationLevelPermissions.can(permission),
    [],
  );

  const canMutate = useCallback(
    (action: string): boolean =>
      can(`integrates_api_mutations_${action}_mutate`),
    [can],
  );

  const canResolve = useCallback(
    (action: string): boolean =>
      can(`integrates_api_resolvers_${action}_resolve`),
    [can],
  );

  return {
    error,
    loading,
    permissionMethods: {
      can,
      canMutate,
      canResolve,
      refresh,
    },
    permissions: organizationLevelPermissions,
  };
};

export { useOrgPermissions };
